import store from '../store/store';
import { Locale } from '../constants/user';
import { localize } from '../utilities/validator';
import { getCookie, setCookie } from '../../fcoModules/utilities';

// List of additional message resources by locale.
// This should represent a complete index of languages we support
function getLocaleMessages(locale) {
    if (!getLocaleMessages.cache) {
        getLocaleMessages.cache = {};
    }

    const mapDefaultMessagesExport = ({ default: defaultExport } = {}) => defaultExport || {};
    let request = getLocaleMessages.cache[locale];

    if (!request) {
        if (locale === Locale.EN) {
            request = import('../../locales/en.json').then(mapDefaultMessagesExport);
        } else if (locale === Locale.ES) {
            request = import('../../locales/es.json').then(mapDefaultMessagesExport);
        }
        getLocaleMessages.cache[locale] = request;
    }

    return request;
}

const storageLocaleKey = 'locale';

/**
 * Retrieves the saved locale from cookies, and as a fallback from localStorage, handling either a raw locale string or a JSON-stringified value.
 * @returns string
 */
function getStoredLocale() {
    let storedLocale = getCookie(storageLocaleKey);

    if (!storedLocale) {
        storedLocale = localStorage.getItem(storageLocaleKey);
    }

    if (!storedLocale) return null;
    try {
        return JSON.parse(storedLocale);
    } catch (e) {
        return storedLocale;
    }
}

function saveLocale(locale) {
    setCookie(storageLocaleKey, locale, 10 * 365);
}

/**
 * Internationalization Plugin for FCO
 *      - registers a Vuex store module: 'i18n'
 *          - state contains the current locale and messages for that locale
 *          - changeLocale() action to switch locales and get updated messages
 *          - localeOptions getter provides list of value/label pairs of supported locales
 *          - other getters for checking against specific locales
 *      - adds the `fcoM` global mixin for referencing messages by key
 */
export default {
    install(Vue) {
        const isLocaleEnglish = (locale) => locale === Locale.EN;
        const isLocaleSpanish = (locale) => locale === Locale.ES;
        const isValidLocale = (locale) => isLocaleEnglish(locale) || Object.values(Locale).includes(locale);

        store.registerModule('i18n', {
            namespaced: true,
            state: {
                locale: Locale.EN,
                messages: {},
            },
            mutations: {
                setLocale(state, locale) {
                    if (!isValidLocale(locale)) return;
                    state.locale = locale;
                },
                setMessages(state, { locale, messages }) {
                    if (!isValidLocale(locale)) return;
                    state.messages[locale] = messages;
                },
            },
            actions: {
                async changeLocale({ commit, state, getters, dispatch }, { locale = Locale.EN, cache = true } = {}) {
                    // If we don't have messages for a given locale, fall back to English.
                    const validLocale = !isValidLocale(locale) ? Locale.EN : locale;

                    if (getters.hasLocale(locale) && validLocale === state.locale) return;

                    // This check allows the language to be changed without saving the selection in cookies
                    // (i.e. if feature is not enabled but user changes the language on the terms & conditions page)
                    if (cache) {
                        saveLocale(JSON.stringify(validLocale));
                    }

                    if (!getters.hasLocale(validLocale)) {
                        await dispatch('requestLocale', validLocale);
                    }

                    commit('setLocale', validLocale);

                    localize(validLocale);
                },
                async selectDefaultLocale({ dispatch }, localeOverride) {
                    // Determine the default locale in the following order:
                    // 1. localeOverride argument
                    // 2. stored locale in localStorage (from previous visit)
                    // 3. browser default
                    // 4. fallback to English
                    const defaultLocale =
                        [localeOverride, getStoredLocale(), navigator.language]
                            .map((locale) => {
                                if (typeof locale !== 'string') return null;
                                return locale.split('-')[0].toLowerCase();
                            })
                            .find((locale) => isValidLocale(locale)) || Locale.EN;
                    return dispatch('changeLocale', { locale: defaultLocale });
                },
                async requestLocale({ commit, getters }, locale = Locale.EN) {
                    if (!isValidLocale(locale) || getters.hasLocale(locale)) return;

                    const messages = await getLocaleMessages(locale);
                    commit('setMessages', { locale, messages });
                },
            },
            getters: {
                localeOptions: (state, getters) => [
                    { value: Locale.EN, label: getters.fcoM('common.english', 'English') || '' },
                    { value: Locale.ES, label: getters.fcoM('common.espanol', 'Español') || '' },
                ],
                isLocaleEnglish: ({ locale }) => isLocaleEnglish(locale),
                isLocaleSpanish: ({ locale }) => isLocaleSpanish(locale),
                hasLocale:
                    ({ messages }) =>
                    (locale) =>
                        Object.prototype.hasOwnProperty.call(messages, locale) && Object.keys(messages[locale]).length,
                fcoM:
                    ({ locale, messages }) =>
                    (key, fallback = messages[Locale.EN]?.[key] || '') => {
                        if (locale === Locale.EN) return fallback;
                        return messages[locale]?.[key] || fallback;
                    },
            },
        });

        // Register the `fcoM` mixin for referencing messages by key in Vue components
        Vue.mixin({
            computed: {
                fcoM() {
                    return this.$store.getters['i18n/fcoM'];
                },
            },
        });
    },
};
